import type { AnalyticsApi } from '@liks/lac-svc-api/esm/apis/AnalyticsApi';
import type {
  ChatbotRankingDTO,
  ChatbotTenantAnalyticsDTO,
  KnowledgeBaseDocumentTenantAnalyticsDTO,
  PlatformChatRankingDTO,
  PlatformChatTenantAnalyticsDTO,
  TenantOverviewDTO,
} from '@liks/lac-svc-api/esm/models/index';
import {
  LAC_TOP_RANKING_LIMIT,
  type LacAnalyticsListOptions,
  type LacKnowledgeBaseDocumentAnalyticsOptions,
  type LacTenantOverviewDateRange,
  type LacTopChatbotsOptions,
  type LacTopPlatformChatsOptions,
} from './lacAnalyticsServiceTypes';

export async function listTenantOverviews(
  analyticsApi: AnalyticsApi,
  range?: LacTenantOverviewDateRange,
): Promise<TenantOverviewDTO[]> {
  return analyticsApi.deLiksAnalyticsBoundaryAnalyticsResourceListTenantOverviews(
    {
      fromDate: range?.fromDate,
      toDate: range?.toDate,
    },
  );
}

export async function getTenantOverview(
  analyticsApi: AnalyticsApi,
  tenantName: string,
  range?: LacTenantOverviewDateRange,
): Promise<TenantOverviewDTO | null> {
  const trimmedName = tenantName.trim();
  if (trimmedName === '') {
    return null;
  }

  const page =
    await analyticsApi.deLiksAnalyticsBoundaryAnalyticsResourceListTenantOverviews(
      {
        tenantName: trimmedName,
        fromDate: range?.fromDate,
        toDate: range?.toDate,
      },
    );

  return page[0] ?? null;
}

export async function listChatbotAnalytics(
  analyticsApi: AnalyticsApi,
  { tenantName, range, groupByKnowledgeBase }: LacAnalyticsListOptions,
): Promise<ChatbotTenantAnalyticsDTO[]> {
  const trimmedName = tenantName.trim();
  if (trimmedName === '') {
    return [];
  }

  return analyticsApi.deLiksAnalyticsBoundaryAnalyticsResourceListChatbotAnalytics(
    {
      tenantName: trimmedName,
      fromDate: range?.fromDate,
      toDate: range?.toDate,
      groupByKnowledgeBase,
    },
  );
}

export async function listPlatformChatAnalytics(
  analyticsApi: AnalyticsApi,
  { tenantName, range, groupByKnowledgeBase }: LacAnalyticsListOptions,
): Promise<PlatformChatTenantAnalyticsDTO[]> {
  const trimmedName = tenantName.trim();
  if (trimmedName === '') {
    return [];
  }

  return analyticsApi.deLiksAnalyticsBoundaryAnalyticsResourceListPlatformChatAnalytics(
    {
      tenantName: trimmedName,
      fromDate: range?.fromDate,
      toDate: range?.toDate,
      groupByKnowledgeBase,
    },
  );
}

export async function listTopChatbots(
  analyticsApi: AnalyticsApi,
  {
    tenantName,
    criteria,
    knowledgeBaseId,
    range,
    limit = LAC_TOP_RANKING_LIMIT,
  }: LacTopChatbotsOptions,
): Promise<ChatbotRankingDTO[]> {
  const trimmedName = tenantName?.trim() ?? '';
  const trimmedKnowledgeBaseId = knowledgeBaseId?.trim() ?? '';

  return analyticsApi.deLiksAnalyticsBoundaryAnalyticsResourceListTopChatbots({
    criteria,
    tenantName: trimmedName === '' ? undefined : trimmedName,
    limit,
    fromDate: range?.fromDate,
    toDate: range?.toDate,
    knowledgeBaseId:
      trimmedKnowledgeBaseId === '' ? undefined : trimmedKnowledgeBaseId,
  });
}

export async function listTopPlatformChats(
  analyticsApi: AnalyticsApi,
  {
    tenantName,
    criteria,
    knowledgeBaseId,
    range,
    limit = LAC_TOP_RANKING_LIMIT,
  }: LacTopPlatformChatsOptions,
): Promise<PlatformChatRankingDTO[]> {
  const trimmedName = tenantName.trim();
  if (trimmedName === '') {
    return [];
  }

  const trimmedKnowledgeBaseId = knowledgeBaseId?.trim() ?? '';

  return analyticsApi.deLiksAnalyticsBoundaryAnalyticsResourceListTopPlatformChats(
    {
      criteria,
      tenantName: trimmedName,
      limit,
      fromDate: range?.fromDate,
      toDate: range?.toDate,
      knowledgeBaseId:
        trimmedKnowledgeBaseId === '' ? undefined : trimmedKnowledgeBaseId,
    },
  );
}

export async function listKnowledgeBaseDocumentAnalytics(
  analyticsApi: AnalyticsApi,
  { tenantName, range }: LacKnowledgeBaseDocumentAnalyticsOptions = {},
): Promise<KnowledgeBaseDocumentTenantAnalyticsDTO[]> {
  const trimmedName = tenantName?.trim() ?? '';

  return analyticsApi.deLiksAnalyticsBoundaryAnalyticsResourceListKnowledgeBaseDocumentAnalytics(
    {
      tenantName: trimmedName === '' ? undefined : trimmedName,
      fromDate: range?.fromDate,
      toDate: range?.toDate,
    },
  );
}
